import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { ArrowComponent } from '@fullyops/shared/components/arrow/component';
import { AutocompletingSelectComponent } from '@fullyops/shared/components/autocompleting-select/component';
import { PlusButtonComponent } from '@fullyops/shared/components/plus-button/plus-button.component';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextModule } from 'angular-i18next';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { normalizeForSearch } from '@fullyops/shared/normalize-for-search';

export interface PartChoice {
  id: string;
  name: string;
  ref?: string;
}

export interface CountedPartChoice {
  part: PartChoice;
  quantity: number;
}

@Component({
  selector: 'fo-parts-selector',
  templateUrl: './parts-selector.component.html',
  styleUrl: './parts-selector.component.scss',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    I18NextModule,
    FontAwesomeModule,
    AutocompletingSelectComponent,
    PlusButtonComponent,
    ArrowComponent,
  ],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: PartsSelectorComponent,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
})
export class PartsSelectorComponent
  implements
    OnInit,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<CountedPartChoice[]>
{
  @Input() label: string;
  @Input() choices: Observable<PartChoice[]>;
  @Input() choiceOptionTemplate: TemplateRef<any>;
  @Input() matchPredicate = (v: PartChoice, pattern: string) =>
    normalizeForSearch(v.name).indexOf(pattern) !== -1;
  @Input() canCreate = false;
  @Output() onCreate = new EventEmitter<string>();

  private changes$ = new Subject<CountedPartChoice[]>();
  protected filteredChoices$ = new BehaviorSubject<PartChoice[]>([]);
  private countedPartChoices$ = new BehaviorSubject<CountedPartChoice[]>([]);
  private subs = new Subscription();
  private onTouched = () => {};
  protected addPartControl = new FormControl<PartChoice>(null);

  @ViewChild('autocompleteField')
  private autocomplete: AutocompletingSelectComponent<{
    id: string;
    name: string;
  }>;

  get literalText(): string {
    return this.autocomplete.literalText;
  }

  set literalText(s: string) {
    this.autocomplete.literalText = s;
  }

  protected onAddQuantity(n: number, index: number) {
    this.updateSelections((s) => {
      const newS = [...s];
      newS[index] = { ...s[index], quantity: s[index].quantity + n };
      return newS;
    });
  }

  protected onSetQuantity(ev: Event, index: number) {
    this.updateSelections((s) => {
      const newS = [...s];
      newS[index] = {
        ...s[index],
        quantity: ((ev as InputEvent).target as HTMLInputElement).valueAsNumber,
      };
      return newS;
    });
  }

  protected onRemove(i: number) {
    this.updateSelections((s) => {
      const newS = [...s];
      newS.splice(i, 1);
      return newS;
    });
  }

  protected createOption(value: string) {
    if (!value || value.trim() === '') {
      return;
    }
    this.onCreate.next(value);
  }

  protected onClickCreate() {
    const name = this.addInput?.literalText;
    this.createOption(name);
  }

  private updateSelections(
    change: (s: CountedPartChoice[]) => CountedPartChoice[],
  ) {
    const newValue = change(this.value);
    this.writeValue(newValue);
  }

  // implementation of ControlValueAccessor to enable use with reactive forms

  writeValue(v: CountedPartChoice[]) {
    this.value = v;
    this.countedPartChoices$.next(v);
    this.onTouched();
    this.changes$.next(v);
  }

  registerOnChange(f: (v: CountedPartChoice[]) => void): void {
    this.subs.add(this.changes$.subscribe(f));
  }

  registerOnTouched(f: () => void): void {
    this.onTouched = f;
  }

  protected fieldsDisabled = false;

  setDisabledState(isDisabled: boolean): void {
    this.fieldsDisabled = isDisabled;
  }

  ngOnInit() {
    this.subs.add(
      // Add a part entry whenever the user explicity selects a part from the field.
      this.addPartControl.valueChanges.subscribe((p) => {
        // Since we use the requireSelection attribute on the underlying mat-autocomplete,
        // when the field is unfocused - including by pressing the add button - without
        // making a selection, its value resets to null.
        if (p == null) {
          return;
        }
        this.updateSelections((s) => [...s, { part: p, quantity: 1 }]);
        this.addPartControl.setValue(null, { emitEvent: false });
      }),
    );

    const filteredPartChoices$ = combineLatest([
      this.choices,
      this.countedPartChoices$,
    ]).pipe(
      map(([allChoices, selected]) => {
        const selectedIds = new Set(selected.map((s) => s.part.id));
        return allChoices.filter((choice) => !selectedIds.has(choice.id));
      }),
    );

    filteredPartChoices$.subscribe(this.filteredChoices$);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  // implementation of MatFormFieldControl

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
  ) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  @ViewChild(AutocompletingSelectComponent, { static: false })
  private addInput: AutocompletingSelectComponent<PartChoice>;

  value: CountedPartChoice[] = [];

  get stateChanges(): Observable<void> {
    return this.changes$.asObservable().pipe(map(() => void 0));
  }

  static nextId = 0;

  @HostBinding() id = 'fo-parts-selector-' + PartsSelectorComponent.nextId++;

  get placeholder() {
    return '';
  }

  get focused() {
    return this.addInput.focused;
  }

  get empty() {
    return this.value.length === 0;
  }

  get shouldLabelFloat() {
    return true;
  }

  get required() {
    return this.ngControl.control.hasValidator(Validators.required);
  }

  get disabled() {
    return this.ngControl.disabled;
  }

  get errorState() {
    return this.ngControl.touched && this.ngControl.errors !== null;
  }

  get controlType() {
    return 'fo-parts-selector';
  }

  setDescribedByIds(ids: string[]): void {
    this._elementRef.nativeElement.setAttribute(
      'aria-describedby',
      ids.join(' '),
    );
  }

  onContainerClick(_: MouseEvent): void {}

  protected faScrewdriverWrench = faScrewdriverWrench;
}
